/**
 * Снимок режима «вы работаете под другим пользователем» (AK-470).
 *
 * **Источник правды — сервер, не этот файл.** `/auth/me` отдаёт
 * `impersonation.user_full_name` и `impersonation.mode` для входа под
 * конкретным пользователем (PR #578, `ImpersonationInfo`): признак собран из
 * claim'ов токена, поэтому приходит в ЛЮБОЙ вкладке — в том числе открытой
 * через `Ctrl+T`, где `sessionStorage` пуст. Плашка держится на нём.
 *
 * Зачем тогда снимок. Ровно за одним: показать плашку в первый кадр той
 * вкладки, где в режим вошли, — между жёстким переходом на «/» и ответом
 * `/auth/me`. Как только ответ пришёл, слово сервера главнее: сказал «режима
 * нет» — снимок стирается (см. `AppShell`), а не спорит с ним.
 *
 * Снимок — **подпись плашки, а не право доступа**: режим «только чтение»
 * держит сервер (гейт в `current_user_optional`), подделка записи в
 * storage ничего не открывает.
 */

const KEY = 'ikolhoz.impersonation';

export type ImpersonationMode = 'read' | 'write';

export interface ImpersonationSnapshot {
  /** ФИО пользователя, под которым работаем. */
  user_full_name: string;
  /** `read` — только чтение, `write` — полный доступ. Считает сервер. */
  mode: ImpersonationMode;
  tenant_name?: string | null;
  /** ISO-8601. Просроченный снимок читатель отбрасывает. */
  expires_at?: string | null;
}

function isMode(value: unknown): value is ImpersonationMode {
  return value === 'read' || value === 'write';
}

export function writeImpersonationSnapshot(s: ImpersonationSnapshot): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // private mode / quota — плашка просто не появится, вход не ломаем
  }
}

/**
 * Что про режим сказал сервер в последнем `/auth/me` этой вкладки.
 *
 * Живёт в памяти модуля, а не в storage: это не снимок и не кэш, а «знаю ли
 * я прямо сейчас, что человек под чужой учёткой». В новой вкладке снимка нет,
 * и без этого флага `api.ts` не понял бы, кого уводить со страницы, когда
 * сервер объявит конец режима заголовком.
 */
let serverSaysActive = false;

/** Вызывает `AppShell` на каждый ответ `/auth/me`. */
export function setImpersonationActiveFromServer(active: boolean): void {
  serverSaysActive = active;
}

export function clearImpersonationSnapshot(): void {
  // Снимок стирают ровно там, где режим кончился (выход, конец окна, смерть
  // сессии) — значит и слово сервера про «активен» больше не действует.
  serverSaysActive = false;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/**
 * Активен ли режим «вы работаете под …» прямо сейчас.
 *
 * Нужен `api.ts`, когда сервер объявляет заголовком, что режим закончился:
 * уводить человека со страницы стоит только если плашка ему этот режим и
 * обещала. Обновлять сессию режим больше не запрещает — сервер продлевает
 * её с сохранением режима сам, а его конец объявляет явно.
 *
 * Сначала смотрим слово сервера (`/auth/me`), потом снимок: во вкладке,
 * открытой отдельно, снимка нет, а режим есть — и уводить оттуда надо ровно
 * так же, иначе человек остаётся под чужой учёткой на странице,
 * которую сервер уже отдаёт от имени админа.
 */
export function isImpersonationActive(): boolean {
  return serverSaysActive || readImpersonationSnapshot() !== null;
}

/** Снимок или null (нет / повреждён / просрочен — тогда ещё и удаляем). */
export function readImpersonationSnapshot(): ImpersonationSnapshot | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearImpersonationSnapshot();
    return null;
  }
  if (!parsed || typeof parsed !== 'object') {
    clearImpersonationSnapshot();
    return null;
  }

  const s = parsed as Partial<ImpersonationSnapshot>;
  if (typeof s.user_full_name !== 'string' || !isMode(s.mode)) {
    clearImpersonationSnapshot();
    return null;
  }
  if (s.expires_at) {
    const ts = Date.parse(s.expires_at);
    // Сессия истекла — плашка врала бы про режим, которого уже нет.
    if (Number.isFinite(ts) && ts <= Date.now()) {
      clearImpersonationSnapshot();
      return null;
    }
  }
  return {
    user_full_name: s.user_full_name,
    mode: s.mode,
    tenant_name: s.tenant_name ?? null,
    expires_at: s.expires_at ?? null,
  };
}
